import { useEffect, useState } from 'react'
import { Alert, Button, Flex, InputNumber, Modal, Segmented, Space, Typography } from 'antd'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { formatDate } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { fetchVoucherPrintSheet, type VoucherBatch, type VoucherPrintSheetData } from './voucherApi'
import {
  LAYOUTS,
  MAX_CARDS_PER_PRINT,
  PAGE,
  cardsPerPage,
  clampRange,
  loadLayoutChoice,
  paginate,
  saveLayoutChoice,
  sizeLabel,
  type SheetLayoutId,
} from './voucherPrintLayout'
import { imagesReady } from './printReady'
import { PrintPages, SheetPage, type CardMeta } from './VoucherSheetPages'

interface VoucherPrintSheetProps {
  /** `null` — oyna yopiq */
  batch: VoucherBatch | null
  planName: string | null
  onClose: () => void
}

function cardHintKey(sheet: VoucherPrintSheetData): string {
  if (sheet.credentialMode === 'LOGIN_PASSWORD') {
    return sheet.printQr ? 'voucher.print.cardHintLogin' : 'voucher.print.cardHintLoginNoQr'
  }
  return sheet.printQr ? 'voucher.print.cardHint' : 'voucher.print.cardHintNoQr'
}

/**
 * Kartalarni A4 varaqqa chop etish — ixcham 5×8 yoki standart 2×5
 * (`voucherPrintLayout.ts`). Kodlar serverdan olinadi: har so'rov auditga
 * yoziladi, shuning uchun katta partiya oraliq bilan
 * ({@link MAX_CARDS_PER_PRINT} tadan) so'raladi, kichigi ochilishi bilan.
 */
export function VoucherPrintSheet({ batch, planName, onClose }: VoucherPrintSheetProps) {
  const { t } = useTranslation()
  const [range, setRange] = useState({ from: 1, to: 1 })
  const [sheet, setSheet] = useState<VoucherPrintSheetData | null>(null)
  const [logoFailed, setLogoFailed] = useState(false)
  const [layoutId, setLayoutId] = useState<SheetLayoutId>(loadLayoutChoice)
  const layout = LAYOUTS[layoutId]

  const load = useMutation({
    mutationFn: (request: { batchId: string; from: number; to: number }) =>
      fetchVoucherPrintSheet(request.batchId, { from: request.from, to: request.to }),
    onSuccess: setSheet,
  })
  const { mutate, reset } = load

  const batchId = batch?.id ?? null
  const quantity = batch?.quantity ?? 0

  useEffect(() => {
    setSheet(null)
    setLogoFailed(false)
    reset()
    if (batchId === null) return
    const initial = clampRange(1, quantity, quantity)
    setRange(initial)
    if (quantity <= MAX_CARDS_PER_PRINT) mutate({ batchId, ...initial })
  }, [batchId, quantity, mutate, reset])

  const requestRange = (): void => {
    if (batchId === null) return
    const next = clampRange(range.from, range.to, quantity)
    setRange(next)
    setSheet(null)
    mutate({ batchId, ...next })
  }

  const chooseLayout = (id: SheetLayoutId): void => {
    setLayoutId(id)
    saveLayoutChoice(id)
  }

  const pages = sheet ? paginate(sheet.cards, layout) : []
  const meta: CardMeta | null = sheet
    ? {
        batchNo: sheet.batchNo,
        quantity: sheet.quantity,
        note: sheet.note,
        planName: planName ?? sheet.planId,
        price: formatMoneyValue(sheet.price),
        validUntil: sheet.validUntil ? t('voucher.print.validUntil', { date: formatDate(sheet.validUntil) }) : null,
        issuer: sheet.companyName,
        branch: sheet.branchName,
        logoUrl: sheet.logoUrl,
        instructions: sheet.instructions,
        hint: t(cardHintKey(sheet)),
        portalUrl: sheet.portalUrl,
        printQr: sheet.printQr,
        loginLabel: t('voucher.print.login'),
        passwordLabel: t('voucher.print.password'),
      }
    : null

  const print = async (): Promise<void> => {
    const ready = await imagesReady()
    setLogoFailed(!ready)
    if (ready) window.print()
  }

  return (
    <Modal
      open={batch !== null}
      title={batch ? t('voucher.print.titleNo', { no: batch.batchNo }) : t('voucher.print.title')}
      okText={t('voucher.print.print')}
      cancelText={t('common.close')}
      onOk={() => void print()}
      onCancel={onClose}
      okButtonProps={{ disabled: pages.length === 0 }}
      width={880}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Flex gap={8} align="center" wrap>
          <Typography.Text>{t('voucher.print.layout')}</Typography.Text>
          <Segmented<SheetLayoutId>
            value={layoutId}
            onChange={chooseLayout}
            options={[
              { value: 'compact', label: t('voucher.print.layoutCompact', { perPage: cardsPerPage(LAYOUTS.compact) }) },
              { value: 'standard', label: t('voucher.print.layoutStandard', { perPage: cardsPerPage(LAYOUTS.standard) }) },
            ]}
          />
        </Flex>
        <Flex gap={8} align="center" wrap>
          <Typography.Text>{t('voucher.print.range')}</Typography.Text>
          <InputNumber
            min={1}
            max={quantity}
            value={range.from}
            onChange={(value) => setRange((r) => ({ ...r, from: value ?? 1 }))}
            aria-label={t('voucher.print.rangeFrom')}
          />
          <Typography.Text type="secondary">—</Typography.Text>
          <InputNumber
            min={1}
            max={quantity}
            value={range.to}
            onChange={(value) => setRange((r) => ({ ...r, to: value ?? r.from }))}
            aria-label={t('voucher.print.rangeTo')}
          />
          <Button onClick={requestRange} loading={load.isPending}>
            {t('voucher.print.load')}
          </Button>
        </Flex>
        <Typography.Text type="secondary">
          {t('voucher.print.rangeHint', { max: MAX_CARDS_PER_PRINT, quantity })}
        </Typography.Text>

        {load.isError ? <Alert type="error" showIcon message={translateError(load.error, t)} /> : null}

        {logoFailed ? (
          <Alert
            type="error"
            showIcon
            message={t('voucher.print.logoFailedTitle')}
            description={t('voucher.print.logoFailedText')}
          />
        ) : null}

        {sheet && sheet.printQr && !sheet.portalUrl ? (
          <Alert
            type="warning"
            showIcon
            message={t('voucher.print.noPortalTitle')}
            description={t('voucher.print.noPortalText')}
          />
        ) : null}

        {sheet && sheet.cards.length > 0 ? (
          <Alert
            type="warning"
            showIcon
            message={t('voucher.print.warnTitle')}
            description={
              <>
                {t(sheet.credentialMode === 'LOGIN_PASSWORD' ? 'voucher.print.warnTextCredentials' : 'voucher.print.warnText', {
                  count: sheet.cards.length,
                })}{' '}
                {t('voucher.print.pages', { count: pages.length, perPage: cardsPerPage(layout), size: sizeLabel(layout) })}
                <br />
                {t('voucher.print.printTip')}
              </>
            }
          />
        ) : null}

        {sheet && sheet.skipped > 0 ? (
          <Alert type="info" showIcon message={t('voucher.print.skipped', { count: sheet.skipped })} />
        ) : null}

        {sheet && sheet.cards.length === 0 ? (
          <Typography.Text type="secondary">{t('voucher.print.nothing')}</Typography.Text>
        ) : null}

        {meta && pages[0] ? (
          <div style={{ overflowX: 'auto' }}>
            <Typography.Text type="secondary">{t('voucher.print.preview')}</Typography.Text>
            <div style={{ boxShadow: '0 0 0 1px #d9d9d9', width: `${PAGE.widthMm}mm`, marginTop: 8 }}>
              <SheetPage cards={pages[0]} meta={meta} layout={layout} />
            </div>
          </div>
        ) : null}
      </Space>

      {meta && sheet && sheet.cards.length > 0 ? <PrintPages cards={sheet.cards} meta={meta} layout={layout} /> : null}
    </Modal>
  )
}
